import {
  downloadAnalyticsExport,
  type AnalyticsExportFormat,
  type AnalyticsExportTableSection,
} from "../../utils/analyticsFileExport";
import type {
  DailyRevenuePoint,
  DiscountImpactPoint,
  ServiceDetailAnalytics,
  ServiceReviewRow,
  ServiceReviewSummary,
} from "./serviceDetailTypes";

type ServiceExportContext = {
  serviceName: string;
  serviceId: string;
  from: string;
  to: string;
};

function buildServiceSection(
  params: ServiceExportContext,
): AnalyticsExportTableSection {
  return {
    title: "Service",
    headers: ["Field", "Value"],
    rows: [
      ["Service ID", params.serviceId],
      ["Service Name", params.serviceName],
      ["From", params.from],
      ["To", params.to],
    ],
  };
}

function buildServiceDetailSections(params: {
  serviceName: string;
  serviceId: string;
  from: string;
  to: string;
  analytics: ServiceDetailAnalytics;
}): AnalyticsExportTableSection[] {
  const { serviceName, serviceId, from, to, analytics } = params;
  const { popularity, dailyRevenue, discountImpact } = analytics;

  return [
    buildServiceSection({ serviceName, serviceId, from, to }),
    {
      title: "Summary",
      headers: ["Total Revenue", "Guest Used %", "Completed", "Canceled"],
      rows: [
        [
          popularity.totalRevenue,
          popularity.guestsUsedPercent ?? "",
          popularity.statusCounts[0]?.count ?? 0,
          popularity.statusCounts[1]?.count ?? 0,
        ],
      ],
    },
    {
      title: "Daily Revenue",
      headers: ["Day", "Revenue"],
      rows: dailyRevenue.map((point) => [point.day, point.revenue]),
    },
    {
      title: "Discount Impact",
      headers: ["Day", "Revenue", "Discount"],
      rows: discountImpact.map((point) => [
        point.label,
        point.revenue,
        point.discount,
      ]),
    },
    {
      title: "Discount Totals",
      headers: ["Discount saved %", "Total profit"],
      rows: [[analytics.discountSavedPercent, analytics.discountTotalProfit]],
    },
    buildRelatedReviewsSummarySection(analytics.reviewSummary),
    buildRelatedReviewsRowsSection(analytics.reviews),
  ];
}

function buildDailyRevenueSection(
  dailyRevenue: DailyRevenuePoint[],
): AnalyticsExportTableSection {
  return {
    title: "Daily Revenue",
    headers: ["Day", "Revenue"],
    rows: dailyRevenue.map((point) => [point.day, point.revenue]),
  };
}

function buildDiscountImpactSection(
  discountImpact: DiscountImpactPoint[],
): AnalyticsExportTableSection {
  return {
    title: "Discount Impact",
    headers: ["Day", "Revenue", "Discount"],
    rows: discountImpact.map((point) => [
      point.label,
      point.revenue,
      point.discount,
    ]),
  };
}

function buildDiscountTotalsSection(params: {
  savedPercent: number;
  totalProfit: number;
}): AnalyticsExportTableSection {
  return {
    title: "Discount Totals",
    headers: ["Discount saved %", "Total profit"],
    rows: [[params.savedPercent, params.totalProfit]],
  };
}

function buildRelatedReviewsSummarySection(
  summary: ServiceReviewSummary,
): AnalyticsExportTableSection {
  return {
    title: "Related Reviews Summary",
    headers: [
      "Overall Rating",
      "Positive/Neutral %",
      "Negative %",
      "Reviews Count",
    ],
    rows: [
      [
        summary.overallRating,
        summary.positiveNeutralPercent,
        summary.negativePercent,
        summary.reviewsCount,
      ],
    ],
  };
}

function buildRelatedReviewsRowsSection(
  reviews: ServiceReviewRow[],
): AnalyticsExportTableSection {
  return {
    title: "Related Reviews",
    headers: [
      "Guest",
      "Country",
      "Responsibility",
      "Feedback Time",
      "Feedback",
      "Rating",
    ],
    rows: reviews.map((review) => [
      review.guestName,
      review.countryCode,
      review.responsibility,
      review.feedbackTime,
      review.feedback,
      review.rating,
    ]),
  };
}

export function downloadServiceDetailExport(
  format: AnalyticsExportFormat,
  params: {
    serviceName: string;
    serviceId: string;
    from: string;
    to: string;
    analytics: ServiceDetailAnalytics;
  },
): void {
  downloadAnalyticsExport(
    format,
    buildServiceDetailSections(params),
    `analytics-service-${params.serviceId}-${params.from}-${params.to}`,
  );
}

export function downloadServiceDailyRevenueExport(
  format: AnalyticsExportFormat,
  params: ServiceExportContext & {
    dailyRevenue: DailyRevenuePoint[];
  },
): void {
  downloadAnalyticsExport(
    format,
    [
      buildServiceSection(params),
      buildDailyRevenueSection(params.dailyRevenue),
    ],
    `analytics-service-${params.serviceId}-daily-revenue-${params.from}-${params.to}`,
  );
}

export function downloadServiceDiscountImpactExport(
  format: AnalyticsExportFormat,
  params: ServiceExportContext & {
    discountImpact: DiscountImpactPoint[];
    savedPercent: number;
    totalProfit: number;
  },
): void {
  downloadAnalyticsExport(
    format,
    [
      buildServiceSection(params),
      buildDiscountImpactSection(params.discountImpact),
      buildDiscountTotalsSection({
        savedPercent: params.savedPercent,
        totalProfit: params.totalProfit,
      }),
    ],
    `analytics-service-${params.serviceId}-discount-impact-${params.from}-${params.to}`,
  );
}

export function downloadServiceRelatedReviewsExport(
  format: AnalyticsExportFormat,
  params: ServiceExportContext & {
    reviewSummary: ServiceReviewSummary;
    reviews: ServiceReviewRow[];
  },
): void {
  downloadAnalyticsExport(
    format,
    [
      buildServiceSection(params),
      buildRelatedReviewsSummarySection(params.reviewSummary),
      buildRelatedReviewsRowsSection(params.reviews),
    ],
    `analytics-service-${params.serviceId}-related-reviews-${params.from}-${params.to}`,
  );
}
